import PageHeader from '../../components/PageHeader';
import FilterPanel from '../../components/FilterPanel';
import { useState } from 'react';
import DataTable from '../../components/DataTable';
import { formatDate } from '../../utils/formatters';
import { Button, Typography, Card, Paper, Stack, TextField } from '@mui/material';
import { BarChart as MuiBarChart } from '@mui/x-charts/BarChart';
const toDateInputValue = (date) => date.toISOString().slice(0, 10);
const getDefaultDateRange = () => {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 1);
  return {
    from: toDateInputValue(from),
    to: toDateInputValue(to),
  };
};
const eventTypeLabels = {
  SEARCH_PLACES: 'חיפוש מקומות',
  CLICK_START_NAVIGATION: 'כפתור בדרך למקום',
  FAVOURITE_SEARCH_PLACE: 'פתיחה ממועדפים',
  SUBMIT_REPORT_LOAD: 'שליחת דיווח עומס',
  ADD_FAVORITE: 'הוספה למועדפים',
  REMOVE_FAVORITE: 'הסרה ממועדפים',
  OPEN_SEARCH: 'פתיחת חיפוש',
  CLEAR_SEARCH: 'ניקוי חיפוש',
  OPEN_PLACE: 'פתיחת מקום',
  CLOSE_PLACE: 'סגירת מקום',
  OPEN_ABOUT: 'פתיחת אודות',
  CLOSE_ABOUT: 'סגירת אודות',
  CLICK_NAVIGATE: 'לחיצה על ניווט',
  CLICK_NAVIGATE_FROM_PLACE: 'ניווט ממסך מקום',
  CLICK_NAVIGATE_FROM_ACTIVE_TRACKING: 'ניווט מבדיקת עומס פעילה',
  CLICK_MOVE_TO_MY_LOCATION: 'מעבר למיקום שלי',
  CLICK_BEST_PLACE_TO_GO_NOW: 'המקום הכי כדאי עכשיו',
  CLICK_CATEGORY: 'לחיצה על קטגוריה',
  CLICK_REPORT_LOAD_IN_PLACE: 'כפתור דיווח עומס ממסך מקום',
  CLICK_REPORT_LOAD_IN_MAP: 'כפתור דיווח עומס מהמפה',
  APP_INSTALL: 'התקנת אפליקציה',
  APP_OPEN: 'פתיחת אפליקציה',
  APP_RESUME: 'חזרה לאפליקציה',
  APP_BACKGROUND: 'מעבר לרקע',
  OPEN_FAVOURITE: 'פתיחת מועדפים',
  CLOSE_FAVOURITE: 'סגירת מועדפים',
  NAVIGATION_TIMEOUT: 'סיום ניווט בגלל זמן',
  MOVING_AWAY_FROM_DESTINATION: 'התרחקות מהיעד',
  USER_ARRIVED: 'המשתמש הגיע',
};
const formatEventType = (eventType) => eventTypeLabels[eventType] || eventType || 'לא ידוע';
const userLabel = (user = {}) =>
  user.fullName || user.anonymousId || user.deviceId || 'משתמש לא ידוע';
const sourceLabels = {
  app: 'אפליקציה',
  user: 'משתמש',
  gps: 'GPS',
};
const navigationProviderLabels = {
  waze: 'Waze',
  googleMap: 'Google Maps',
  googleMaps: 'Google Maps',
};
const categoryLabels = {
  park: 'פארק',
  beach: 'חוף',
  supermarket: 'סופרמרקט',
  pharmacy: 'בית מרקחת',
  bank: 'בנק',
  post_office: 'דואר',
  electronics_store: 'חנות אלקטרוניקה',
  shopping_mall: 'קניון',
  restaurant: 'מסעדה',
  cafe: 'בית קפה',
  gym: 'חדר כושר',
  gas_station: 'תחנת דלק',
  default: 'כללי',
};
const formatSource = (source) => sourceLabels[source] || source || 'לא ידוע';
const formatNavigationProvider = (provider) =>
  navigationProviderLabels[provider] || provider || 'לא ידוע';
const formatCategory = (category) => categoryLabels[category] || category || 'לא ידוע';
const formatLoadScore = (score) => (score ? `ציון ${score}` : 'לא ידוע');
const importantEventTypes = [
  'APP_OPEN',
  'CLICK_START_NAVIGATION',
  'CLICK_NAVIGATE_FROM_ACTIVE_TRACKING',
  'CLICK_NAVIGATE_FROM_PLACE',
  'CLICK_REPORT_LOAD_IN_PLACE',
  'CLICK_REPORT_LOAD_IN_MAP',
  'SUBMIT_REPORT_LOAD',
];
const columns = [
  {
    key: 'eventType',
    label: 'סוג',
    render: (event) => formatEventType(event.eventType),
  },
  {
    key: 'source',
    label: 'מקור',
    render: (event) => event.source || '-',
  },
  {
    key: 'user',
    label: 'משתמש',
    render: (event) => event.userId || event.anonymousId || event.deviceId || '-',
  },
  {
    key: 'place',
    label: 'מקום',
    render: (event) => event.metadata?.placeId || '-',
  },
  {
    key: 'createdAt',
    label: 'נוצר',
    render: (event) => formatDate(event.createdAt),
  },
];
const UniqueUsersDialog = ({ open, title, users = [], total = 0, onClose }) => {
  if (!open) return null;
  return (
    <Stack
      onMouseDown={onClose}
      sx={(theme) => ({
        position: 'fixed',
        inset: '0',
        zIndex: '30',
        display: 'grid',
        placeItems: 'center',
        padding: '24px',
        background: 'rgba(21, 33, 31, .56)',
        [theme.breakpoints.down('sm')]: {
          padding: '0',
        },
        backdropFilter: 'blur(6px)',
      })}
    >
      <Stack
        onMouseDown={(event) => event.stopPropagation()}
        dir="rtl"
        sx={(theme) => ({
          width: 'min(760px, calc(100vw - 32px))',
          height: 'calc(100vh - 48px)',
          maxHeight: 'min(720px, calc(100vh - 48px))',
          minHeight: '0',
          display: 'grid',
          gridTemplateRows: 'auto auto minmax(0, 1fr) auto',
          contain: 'layout',
          background: 'var(--panel)',
          borderRadius: '20px',
          boxShadow: 'var(--shadow-lg)',
          overflow: 'hidden',
          textAlign: 'right',
          [theme.breakpoints.down('sm')]: {
            width: '100%',
            height: '100vh',
            maxHeight: '100vh',
            minHeight: '100vh',
            borderRadius: '0',
          },
          border: '1px solid rgba(255,255,255,.45)',
        })}
      >
        <Stack
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
            padding: '20px 24px',
            borderBottom: '1px solid var(--line)',
            [theme.breakpoints.down('sm')]: {
              padding: '16px',
            },
          })}
        >
          <Stack>
            <Typography>{title}</Typography>
            <Typography>
              {total} משתמשים ייחודיים
              {users.length < total ? ` · מוצגים ${users.length} ראשונים` : ''}
            </Typography>
          </Stack>
          <Button
            type="button"
            onClick={onClose}
            sx={{
              width: '42px',
              height: '42px',
              minHeight: '42px',
              borderRadius: '50%',
              padding: '0',
              fontSize: '28px',
              lineHeight: '1',
              background: '#eef2f6',
              color: 'var(--text)',
            }}
          >
            ×
          </Button>
        </Stack>

        <Paper
          sx={(theme) => ({
            minWidth: '0',
            minHeight: '0',
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            scrollbarGutter: 'stable',
            padding: '14px',
            display: 'grid',
            alignContent: 'start',
            gap: '24px',
            [theme.breakpoints.down('sm')]: {
              padding: '14px',
            },
          })}
        >
          {users.length === 0 ? (
            <Typography
              sx={(theme) => ({
                color: 'var(--muted)',
                [theme.breakpoints.down('lg')]: {
                  margin: '4px 0 0',
                  fontSize: '12px',
                  display: 'none',
                },
                margin: '2px 0 0',
                fontSize: '11px',
              })}
            >
              אין משתמשים להצגה
            </Typography>
          ) : (
            <Stack
              sx={{
                display: 'grid',
                gap: '10px',
              }}
            >
              {users.map((user) => (
                <Card
                  key={
                    user.anonymousId || user.deviceId || `${userLabel(user)}-${user.eventsCount}`
                  }
                  sx={{
                    display: 'grid',
                    gridTemplateColumns:
                      'minmax(180px, 1.2fr) minmax(140px, 0.9fr) minmax(180px, 1.2fr)',
                    gap: '12px',
                    alignItems: 'start',
                    padding: '12px',
                    border: '1px solid var(--line)',
                    borderRadius: '12px',
                    background: '#fff',
                    '& div': {
                      minWidth: '0',
                      display: 'grid',
                      gap: '4px',
                    },
                    '& strong': {
                      minWidth: '0',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                    '& small': {
                      minWidth: '0',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: 'var(--muted)',
                      fontSize: '12px',
                    },
                  }}
                >
                  <Stack>
                    <Typography>{userLabel(user)}</Typography>
                    <Typography>{user.platform || 'unknown'}</Typography>
                  </Stack>
                  <Stack>
                    <Typography>{user.eventsCount || 0} אירועים</Typography>
                    <Typography>
                      אירוע אחרון: {user.lastEventAt ? formatDate(user.lastEventAt) : '-'}
                    </Typography>
                  </Stack>
                  <Stack>
                    <Typography title={user.anonymousId || ''}>
                      {user.anonymousId || '-'}
                    </Typography>
                    <Typography title={user.deviceId || ''}>
                      Device: {user.deviceId || '-'}
                    </Typography>
                  </Stack>
                </Card>
              ))}
            </Stack>
          )}
        </Paper>
      </Stack>
    </Stack>
  );
};
const EventTypeCard = ({ item, total, highlighted = false, onOpenUsers }) => {
  return (
    <Card
      sx={String(`panel event-type-card ${highlighted ? 'important' : ''}`)
        .split(/\s+/)
        .map(
          (name) =>
            ({
              panel: {
                background: 'var(--panel)',
                border: '1px solid var(--line)',
                borderRadius: '16px',
                borderColor: 'rgba(20,45,41,.11)',
                boxShadow: 'var(--shadow-sm)',
                backdropFilter: 'blur(10px)',
              },
              'event-type-card': {
                minHeight: '128px',
                display: 'grid',
                gap: '8px',
                alignContent: 'start',
                padding: '16px',
                borderTop: '3px solid var(--primary)',
                '& strong': {
                  color: 'var(--text)',
                  fontSize: '34px',
                  lineHeight: '1',
                },
                '& small': {
                  color: 'var(--muted)',
                },
              },
              important: {
                borderTopColor: '#f79009',
                background: 'linear-gradient(180deg, #fff8ed 0, #fff 52px)',
              },
            })[name],
        )
        .filter(Boolean)}
    >
      <Typography title={item._id || 'unknown'}>{formatEventType(item._id)}</Typography>
      <Typography>{item.count || 0}</Typography>
      <Button
        type="button"
        onClick={() =>
          onOpenUsers({
            title: `משתמשים ייחודיים · ${formatEventType(item._id)}`,
            users: item.users || [],
            total: item.uniqueUsers || 0,
          })
        }
        sx={{
          minHeight: '0',
          display: 'inline',
          padding: '0',
          borderRadius: '0',
          background: 'transparent',
          color: 'var(--primary)',
          fontSize: '13px',
          fontWeight: '900',
          textAlign: 'inherit',
          textDecoration: 'underline',
          textUnderlineOffset: '3px',
        }}
      >
        {item.uniqueUsers || 0} משתמשים ייחודיים
      </Button>
    </Card>
  );
};
const EventTypeSummary = ({ items = [], total = 0, uniqueUsers = 0, uniqueUserDetails = [] }) => {
  const [selectedUsers, setSelectedUsers] = useState(null);
  const itemsByType = new Map(items.map((item) => [item._id || 'unknown', item]));
  const importantItems = importantEventTypes.map(
    (eventType) =>
      itemsByType.get(eventType) || {
        _id: eventType,
        count: 0,
        uniqueUsers: 0,
        users: [],
      },
  );
  const otherItems = items.filter((item) => !importantEventTypes.includes(item._id || 'unknown'));
  const openUsers = ({ title, users, total }) => {
    setSelectedUsers({
      title,
      users,
      total,
    });
  };
  return (
    <Stack
      sx={{
        minHeight: '0',
        display: 'grid',
        gridTemplateRows: 'auto minmax(0, 1fr)',
        gap: '14px',
        overflow: 'hidden',
      }}
    >
      <Stack
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '16px',
          '& strong': {
            color: 'var(--primary-dark)',
            fontSize: '24px',
          },
        }}
      >
        <Stack
          sx={{
            display: 'grid',
            gap: '4px',
            textAlign: 'left',
            '& small': {
              color: 'var(--muted)',
              fontWeight: '800',
            },
          }}
        >
          <Typography>{total} אירועים</Typography>
        </Stack>
      </Stack>

      {items.length === 0 ? (
        <Card
          sx={{
            background: 'var(--panel)',
            border: '1px solid var(--line)',
            borderRadius: '16px',
            borderColor: 'rgba(20,45,41,.11)',
            boxShadow: 'var(--shadow-sm)',
            backdropFilter: 'blur(10px)',
            padding: '28px 16px',
            color: 'var(--muted)',
            textAlign: 'center',
          }}
        >
          אין נתונים בטווח הזה
        </Card>
      ) : (
        <Stack
          sx={{
            minHeight: '0',
            display: 'grid',
            gap: '18px',
            alignContent: 'start',
            overflow: 'auto',
            paddingBottom: '4px',
          }}
        >
          <Stack
            sx={{
              display: 'grid',
              gap: '10px',
            }}
          >
            <Stack
              sx={{
                minHeight: '0',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
                gap: '12px',
                alignContent: 'start',
                paddingBottom: '4px',
              }}
            >
              {importantItems.map((item) => (
                <EventTypeCard
                  key={item._id || 'unknown'}
                  item={item}
                  total={total}
                  highlighted
                  onOpenUsers={openUsers}
                />
              ))}
            </Stack>
          </Stack>

          {otherItems.length ? (
            <>
              <Stack
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto 1fr',
                  alignItems: 'center',
                  gap: '12px',
                  color: 'var(--muted)',
                  fontSize: '12px',
                  fontWeight: '900',
                  '&::before': {
                    content: '""',
                    height: '1px',
                    background: 'var(--line)',
                  },
                  '&::after': {
                    content: '""',
                    height: '1px',
                    background: 'var(--line)',
                  },
                }}
              >
                <Typography></Typography>
              </Stack>
              <Stack
                sx={{
                  display: 'grid',
                  gap: '10px',
                }}
              >
                <Stack
                  sx={{
                    minHeight: '0',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
                    gap: '12px',
                    alignContent: 'start',
                    paddingBottom: '4px',
                  }}
                >
                  {otherItems.map((item) => (
                    <EventTypeCard
                      key={item._id || 'unknown'}
                      item={item}
                      total={total}
                      onOpenUsers={openUsers}
                    />
                  ))}
                </Stack>
              </Stack>
            </>
          ) : null}
        </Stack>
      )}

      <UniqueUsersDialog
        open={Boolean(selectedUsers)}
        title={selectedUsers?.title || ''}
        users={selectedUsers?.users || []}
        total={selectedUsers?.total || 0}
        onClose={() => setSelectedUsers(null)}
      />
    </Stack>
  );
};
const BreakdownCard = ({
  title,
  description,
  items = [],
  total = 0,
  formatLabel = (value) => value,
}) => {
  const labels = items.map((item) => formatLabel(item._id));
  const values = items.map((item) => item.count || 0);
  return (
    <Card
      sx={{
        minWidth: 0,
        p: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        borderTop: '3px solid',
        borderTopColor: 'primary.main',
        borderRadius: 2,
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <Stack mb={2}>
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      </Stack>
      {items.length === 0 ? (
        <Typography
          sx={{
            py: 3,
            color: 'text.secondary',
            textAlign: 'center',
          }}
        >
          אין נתונים להצגה בטווח הזה
        </Typography>
      ) : (
        <MuiBarChart
          layout="horizontal"
          height={Math.max(220, items.length * 46)}
          hideLegend
          grid={{
            vertical: true,
          }}
          yAxis={[
            {
              scaleType: 'band',
              data: labels,
              width: 120,
            },
          ]}
          xAxis={[
            {
              min: 0,
            },
          ]}
          series={[
            {
              data: values,
              label: title,
              color: '#2e90fa',
              valueFormatter: (value) => {
                const count = value ?? 0;
                const percent = total > 0 ? Math.round((count / total) * 100) : 0;
                return `${count} · ${percent}%`;
              },
            },
          ]}
          margin={{
            top: 10,
            right: 20,
            bottom: 30,
            left: 10,
          }}
          sx={{
            direction: 'ltr',
          }}
        />
      )}
    </Card>
  );
};
const AnalyticsBreakdowns = ({ reports = {}, total = 0 }) => {
  const breakdowns = reports.breakdowns || {};
  const daily = [...(breakdowns.daily || reports.daily || [])].reverse();
  return (
    <Stack
      sx={{
        minHeight: '0',
        display: 'grid',
        gridTemplateRows: 'auto minmax(0, 1fr)',
        gap: '14px',
        overflow: 'hidden',
      }}
    >
      <Stack
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '16px',
          '& strong': {
            color: 'var(--primary-dark)',
            fontSize: '24px',
          },
        }}
      >
        <Stack>
          <Typography>פילוחים</Typography>
          <Typography>כל הנתונים כאן מושפעים מטווח התאריכים והפילטרים של האנליטיקס</Typography>
        </Stack>
        <Stack
          sx={{
            display: 'grid',
            gap: '4px',
            textAlign: 'left',
            '& small': {
              color: 'var(--muted)',
              fontWeight: '800',
            },
          }}
        >
          <Typography>{total} אירועים</Typography>
        </Stack>
      </Stack>

      <Stack
        sx={(theme) => ({
          minHeight: '0',
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: '14px',
          alignContent: 'start',
          overflow: 'auto',
          paddingBottom: '4px',
          [theme.breakpoints.down('lg')]: {
            gridTemplateColumns: '1fr',
          },
        })}
      >
        <BreakdownCard
          title="מקומות מובילים"
          description="לפי Place ID"
          items={breakdowns.byPlace || []}
          total={total}
          formatLabel={(placeId) => placeId || 'ללא מקום'}
        />
        <BreakdownCard
          title="ניווט לפי אפליקציה"
          description="Waze / Google Maps כשנשלח מהאפליקציה"
          items={breakdowns.byNavigationProvider || []}
          total={total}
          formatLabel={formatNavigationProvider}
        />
        <BreakdownCard
          title="קטגוריות"
          description="לחיצות קטגוריה ואירועים עם קטגוריה"
          items={breakdowns.byCategory || []}
          total={total}
          formatLabel={formatCategory}
        />
        <BreakdownCard
          title="דיווחי עומס לפי ציון"
          description="רק אירועים ששמרו loadScore"
          items={breakdowns.byLoadScore || []}
          total={total}
          formatLabel={formatLoadScore}
        />
      </Stack>
    </Stack>
  );
};
const AnalyticsPage = ({ analytics, filters, setFilters, loadAnalytics, loading }) => {
  const [activeView, setActiveView] = useState('table');
  const defaultDateRange = getDefaultDateRange();
  const fromValue = filters.from || defaultDateRange.from;
  const toValue = filters.to || defaultDateRange.to;
  return (
    <Stack sx={(theme) => ({ gap: '22px', overflow: 'hidden' })}>
      <PageHeader title={<>אנליטיקס</>} meta={<>{analytics.total} אירועים</>}>
        <FilterPanel className="">
          <TextField
            type="date"
            value={fromValue}
            onChange={(e) =>
              setFilters({
                ...filters,
                from: e.target.value,
              })
            }
            fullWidth
          />
          <TextField
            type="date"
            value={toValue}
            onChange={(e) =>
              setFilters({
                ...filters,
                to: e.target.value,
              })
            }
            fullWidth
          />
          <TextField
            placeholder="Place ID"
            value={filters.placeId}
            onChange={(e) =>
              setFilters({
                ...filters,
                placeId: e.target.value,
              })
            }
            fullWidth
          />
          <TextField
            placeholder="Source"
            value={filters.source}
            onChange={(e) =>
              setFilters({
                ...filters,
                source: e.target.value,
              })
            }
            fullWidth
          />
          <TextField
            placeholder="Event type"
            value={filters.eventType}
            onChange={(e) =>
              setFilters({
                ...filters,
                eventType: e.target.value,
              })
            }
            fullWidth
          />
          <Button onClick={() => loadAnalytics(1)}>סינון</Button>
        </FilterPanel>
      </PageHeader>

      <Stack
        direction="row"
        sx={{
          gap: '6px',
        }}
      >
        <Button type="button" onClick={() => setActiveView('table')}>
          טבלת אירועים
        </Button>

        <Button type="button" onClick={() => setActiveView('summary')}>
          סיכומים
        </Button>

        <Button type="button" onClick={() => setActiveView('breakdowns')}>
          פילוחים
        </Button>
      </Stack>

      {activeView === 'breakdowns' ? (
        <AnalyticsBreakdowns reports={analytics.reports || {}} total={analytics.total || 0} />
      ) : activeView === 'summary' ? (
        <EventTypeSummary
          items={analytics.reports?.byEventType || []}
          total={analytics.total || 0}
          uniqueUsers={analytics.reports?.uniqueUsers || 0}
          uniqueUserDetails={analytics.reports?.uniqueUserDetails || []}
        />
      ) : (
        <DataTable
          columns={columns}
          items={analytics.items}
          rowKey={(event) => event._id || event.id}
          page={analytics.page}
          limit={analytics.limit}
          total={analytics.total}
          onPageChange={loadAnalytics}
          loading={loading}
        />
      )}
    </Stack>
  );
};
export default AnalyticsPage;
